// src/components/login/admin/audit-log-dialog.tsx
//
// Read-only viewer over /auth/admin/audit-log. Answers "did this user ever set
// a password / when did they last sign in / did an admin touch this account"
// from a screen instead of a DB query. The username filter matches both the
// event's own user AND admin-action targets (metadata.target_username).
import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCcw, Download } from "lucide-react";
import { authApi, type AuditLogEntry } from "@/api/auth";
import {
  Banner, Button, CANONICAL_NOT_MEASURED as NOT_MEASURED, Modal, SearchInput,
} from "../../kit";

interface AuditLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional username to pre-filter by (e.g. opened from a user's row). */
  initialUsername?: string;
}

const PAGE_SIZE = 100;

// Events that read as "good" vs "bad" vs "neutral", for a subtle status dot.
function eventTone(event: string): "good" | "bad" | "neutral" {
  if (/fail|lock|error/.test(event)) return "bad";
  if (/success|complete|password_change|register|enrolled/.test(event)) return "good";
  return "neutral";
}

function formatWhen(iso?: string | null): string {
  if (!iso) return NOT_MEASURED;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function targetOf(entry: AuditLogEntry): string | null {
  const m = entry.metadata || {};
  const t = (m["target_username"] ?? m["target_user_id"]) as unknown;
  return t == null ? null : String(t);
}

// CSV export ─ RFC-4180-ish quoting: wrap in quotes and double any embedded
// quotes whenever a field contains a comma, quote, or newline.
const CSV_COLUMNS = [
  "id", "created_at", "event_type", "username", "target", "ip_address",
  "user_agent", "metadata",
] as const;

function csvCell(value: unknown): string {
  const s =
    value == null ? ""
    : typeof value === "object" ? JSON.stringify(value)
    : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows: AuditLogEntry[]): string {
  const lines = rows.map((r) =>
    [r.id, r.created_at ?? "", r.event_type, r.username ?? "", targetOf(r) ?? "",
     r.ip_address ?? "", r.user_agent ?? "", r.metadata ?? {}]
      .map(csvCell)
      .join(","),
  );
  return [CSV_COLUMNS.join(","), ...lines].join("\r\n");
}

export function AuditLogDialog({
  open,
  onOpenChange,
  initialUsername = "",
}: AuditLogDialogProps) {
  const [query,       setQuery]       = useState(initialUsername);
  const [rows,        setRows]        = useState<AuditLogEntry[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const reqId = useRef(0);

  const load = useCallback(async (username: string) => {
    const id = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const data = await authApi.auditLog({
        username: username.trim() || undefined,
        limit: PAGE_SIZE,
      });
      if (id === reqId.current) setRows(data);
    } catch (e: any) {
      if (id === reqId.current) setError(e?.message || "Failed to load the audit log.");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  // Export the current filter to CSV. Pulls a fuller slice (up to 500) rather
  // than only the ~100 shown, so the download isn't silently truncated.
  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError(null);
    try {
      const data = await authApi.auditLog({
        username: query.trim() || undefined,
        limit: 500,
      });
      const csv = toCsv(data);
      // Prepend a UTF-8 BOM so Excel opens accented characters correctly.
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = query.trim() ? `-${query.trim().replace(/[^\w.@-]+/g, "_")}` : "";
      a.href = url;
      a.download = `login-activity${suffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || "Failed to export the audit log.");
    } finally {
      setDownloading(false);
    }
  }, [query]);

  // (Re)load whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setQuery(initialUsername);
      load(initialUsername);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      size="form"
      title="Login activity"
      description="Recent authentication events. Search matches a user's own activity and any admin action taken against them."
    >

        {/* Filter row */}
        <form
          onSubmit={(e) => { e.preventDefault(); load(query); }}
          style={{ display: "flex", gap: 8, alignItems: "center" }}
        >
          <SearchInput
            className="upm-page__search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery("")}
            placeholder="Filter by username or email"
            aria-label="Filter login activity"
          />
          <Button
            variant="secondary"
            type="submit"
            icon={RefreshCcw}
            disabled={loading}
            loading={loading}
            loadingLabel="Searching"
          >
            Search
          </Button>
          <Button
            variant="secondary"
            type="button"
            icon={Download}
            onClick={handleDownload}
            disabled={downloading || rows.length === 0}
            loading={downloading}
            loadingLabel="Preparing"
            title="Download matching events as CSV"
          >
            CSV
          </Button>
        </form>

        {error ? <Banner tone="error">{error}</Banner> : null}

        {/* Results */}
        <div
          style={{
            maxHeight: "52vh",
            overflow: "auto",
            border: "1px solid var(--border)",
            borderRadius: 8,
          }}
        >
          <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", fontSize: "var(--text-sm)" }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "var(--muted-30)", zIndex: 1 }}>
                {["When", "Event", "User", "Target", "IP"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left", padding: "8px 10px",
                      fontSize: "var(--text-xs)", fontWeight: 600,
                      color: "var(--muted-foreground)", textTransform: "uppercase",
                      letterSpacing: ".04em", borderBottom: "1px solid var(--border)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: "center", color: "var(--muted-foreground)" }}>
                    No matching activity.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const tone = eventTone(r.event_type);
                const dot =
                  tone === "bad" ? "var(--infrared-coral)"
                  : tone === "good" ? "var(--signal-green)"
                  : "var(--muted-foreground)";
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", color: "var(--muted-foreground)" }}>
                      {formatWhen(r.created_at)}
                    </td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                        <code style={{ fontSize: "var(--text-xs)" }}>{r.event_type}</code>
                      </span>
                    </td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }} title={r.username || undefined}>
                      {r.username || NOT_MEASURED}
                    </td>
                    <td
                      style={{ padding: "8px 10px", whiteSpace: "nowrap", color: "var(--muted-foreground)" }}
                      title={targetOf(r) || undefined}
                    >
                      {targetOf(r) || NOT_MEASURED}
                    </td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap", color: "var(--muted-foreground)" }}>
                      {r.ip_address || NOT_MEASURED}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <span className="k-meta">Showing up to {PAGE_SIZE} most-recent events</span>
    </Modal>
  );
}
